import React, { useEffect, useState, useCallback } from 'react';
import { apiGet, apiDownload } from '../../lib/api';
import { FileSpreadsheet, Download } from 'lucide-react';
import { formatINR } from '../../lib/format';

export default function GSTRegister() {
  const [range, setRange] = useState({ from: '', to: '' });
  const [type, setType] = useState('all');
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    const params = { type };
    if (range.from) params.from = range.from;
    if (range.to) params.to = range.to;
    apiGet('/finance/gst-register', params).then(setData).catch(() => setData({ items: [], totals: {} }));
  }, [range, type]);
  useEffect(() => { load(); }, [load]);

  const exportExcel = () => {
    const qs = new URLSearchParams({ type, format: 'excel', ...(range.from ? { from: range.from } : {}), ...(range.to ? { to: range.to } : {}) });
    apiDownload(`/finance/gst-register?${qs.toString()}`, `gst_register_${range.from || 'all'}_${range.to || 'all'}.xlsx`);
  };

  const items = data?.items || [];
  const t = data?.totals || {};

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="gst-register-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Finance & Accounts</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">GST Register</h1>
          <p className="text-sm text-slate-600 mt-1">Input &amp; output GST for GSTR-1 / GSTR-3B reference.</p>
        </div>
        <button onClick={exportExcel} data-testid="gst-export-btn" className="px-4 py-2 bg-[#1f6e43] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#185536] flex items-center gap-2"><FileSpreadsheet size={14} /> Export Excel</button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <input type="date" className="border border-slate-200 rounded-sm px-3 py-1.5 text-xs" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="gst-from" />
        <span className="text-slate-400 text-xs">to</span>
        <input type="date" className="border border-slate-200 rounded-sm px-3 py-1.5 text-xs" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="gst-to" />
        <select className="border border-slate-200 rounded-sm px-3 py-1.5 text-xs" value={type} onChange={(e) => setType(e.target.value)} data-testid="gst-type">
          <option value="all">Input + Output</option><option value="output">Output (Sales)</option><option value="input">Input (Purchases)</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="gst-totals">
        {[['Taxable', t.taxable_value], ['IGST', t.igst], ['CGST', t.cgst], ['SGST', t.sgst], ['Total GST', t.total_gst]].map(([l, v]) => (
          <div key={l} className="rounded-sm border border-slate-200 p-3 bg-white"><div className="text-[10px] text-slate-500 font-bold uppercase">{l}</div><div className="text-sm font-display font-extrabold text-[#1F7A6E] mt-1">{formatINR(v || 0)}</div></div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500"><tr>{['Type', 'Date', 'Party', 'Invoice', 'Taxable', 'IGST', 'CGST', 'SGST', 'Total GST', 'HSN/SAC'].map((h) => <th key={h} className="text-left px-3 py-2 font-display font-bold uppercase tracking-wider text-[10px]">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 ? <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">No GST transactions in range.</td></tr> : items.map((r, i) => (
              <tr key={i} data-testid={`gst-row-${i}`}>
                <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm ${r.type === 'output' ? 'bg-emerald-100 text-emerald-700' : 'bg-teal-100 text-teal-700'}`}>{r.type}</span></td>
                <td className="px-3 py-2 text-slate-500">{r.date}</td>
                <td className="px-3 py-2 font-display font-bold">{r.party}</td>
                <td className="px-3 py-2 text-slate-500">{r.invoice_ref}</td>
                <td className="px-3 py-2">{formatINR(r.taxable_value)}</td>
                <td className="px-3 py-2">{r.igst ? formatINR(r.igst) : '—'}</td>
                <td className="px-3 py-2">{r.cgst ? formatINR(r.cgst) : '—'}</td>
                <td className="px-3 py-2">{r.sgst ? formatINR(r.sgst) : '—'}</td>
                <td className="px-3 py-2 font-bold">{formatINR(r.total_gst)}</td>
                <td className="px-3 py-2 text-slate-400">{r.hsn_sac || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
